// ============================================================
// CLUBHUB – Regeln der Beitragsberechnung (rein, ohne Datenbank)
// ============================================================
// Was bei jedem Beitragslauf gleich ist: wie viele Intervalle ein
// Zeitraum hat, wie alt jemand am Stichtag ist, was eine Familie
// einzeln zahlen würde. Musterdaten, Assistent und später der
// Server rechnen mit denselben Funktionen.
// ============================================================
import type { ContactStatus, FeeInterval } from "@/generated/prisma/enums";
import { tagAus } from "@/lib/rechnungen/format";

/** Wer Beiträge zahlt — Interessenten und Kontakte nicht. */
export const BEITRAGSPFLICHTIG: ContactStatus[] = ["ACTIVE", "PASSIVE", "HONORARY"];

/** Alter in vollen Jahren am Stichtag. */
export function alterAm(geburt: string | null, stichtag: string): number | null {
  if (!geburt) return null;
  const g = tagAus(geburt);
  const s = tagAus(stichtag);
  let a = s.getUTCFullYear() - g.getUTCFullYear();
  const m = s.getUTCMonth() - g.getUTCMonth();
  if (m < 0 || (m === 0 && s.getUTCDate() < g.getUTCDate())) a--;
  return a;
}

/** Wie viele volle Monate ein Zeitraum umfasst (01.01.–31.12. = 12). */
export function monateImZeitraum(von: string, bis: string): number {
  const a = tagAus(von);
  const b = tagAus(bis);
  return Math.max(1, (b.getUTCFullYear() - a.getUTCFullYear()) * 12 + (b.getUTCMonth() - a.getUTCMonth()) + 1);
}

const MONATE_JE: Record<FeeInterval, number> = {
  MONTHLY: 1,
  QUARTERLY: 3,
  HALF_YEARLY: 6,
  YEARLY: 12,
  ONCE: 0,
};

/** Wie oft ein Intervall in den Zeitraum passt — ein Jahresbeitrag einmal, ein Monatsbeitrag zwölfmal. */
export function intervalleImZeitraum(intervall: FeeInterval, von: string, bis: string): number {
  if (intervall === "ONCE") return 1;
  return Math.max(1, Math.round(monateImZeitraum(von, bis) / MONATE_JE[intervall]));
}

export const INTERVALL_EINHEIT: Record<FeeInterval, [string, string]> = {
  MONTHLY: ["Monat", "Monate"],
  QUARTERLY: ["Quartal", "Quartale"],
  HALF_YEARLY: ["Halbjahr", "Halbjahre"],
  YEARLY: ["Jahr", "Jahre"],
  ONCE: ["einmalig", "einmalig"],
};

/** Jahressumme einer Beitragsart je Person. */
export function jahresbetrag(betrag: number, intervall: FeeInterval): number {
  if (intervall === "ONCE") return betrag;
  return betrag * (12 / MONATE_JE[intervall]);
}

/**
 * Was jemand OHNE Familientarif zahlen würde — für die Warnung
 * „Familienbeitrag teurer als einzeln“. Es gilt die Beitragsordnung
 * der Musterdaten: Ehrenmitglied frei, bis 17 Jugend, passiv
 * ermäßigt, sonst aktiv.
 */
export function einzelbeitragSchluessel(status: ContactStatus, alter: number | null): string | null {
  if (status === "HONORARY") return "f-6";
  if (alter !== null && alter <= 17) return "f-3";
  if (status === "PASSIVE") return "f-2";
  if (status === "ACTIVE") return "f-1";
  return null;
}

/** „01.01.–31.12.2027“ */
export function zeitraumText(von: string, bis: string): string {
  const a = tagAus(von);
  const b = tagAus(bis);
  const tm = (d: Date) => `${String(d.getUTCDate()).padStart(2, "0")}.${String(d.getUTCMonth() + 1).padStart(2, "0")}.`;
  return a.getUTCFullYear() === b.getUTCFullYear()
    ? `${tm(a)}–${tm(b)}${b.getUTCFullYear()}`
    : `${tm(a)}${a.getUTCFullYear()}–${tm(b)}${b.getUTCFullYear()}`;
}
